import React, { useEffect, useState, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { absoluteInviteUrl, api, BACKEND_URL, whatsappShareUrl } from "@/api";
import { toast } from "sonner";
import { Users, Mail, Image as ImageIcon, BarChart3, Copy, Trash2, LogOut, Plus, Share2, ExternalLink, MousePointerClick, Download, Save, Settings, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useLang } from "@/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import { registry as themeRegistry } from "@/themes/registry";

const Stat = ({ label, value, icon: Icon, testId }) => (
    <div className="rounded-xl bg-white border border-slate-200 p-5 flex items-center gap-4" data-testid={testId}>
        <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center">
            <Icon size={18} />
        </div>
        <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-semibold text-slate-900 mt-0.5 tabular-nums">{value}</p>
        </div>
    </div>
);

async function copyText(text) {
    try {
        if (navigator.clipboard?.writeText && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {}

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-1000px";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
        return document.execCommand("copy");
    } catch {
        return false;
    } finally {
        document.body.removeChild(textarea);
    }
}

function formatClickTime(value, neverLabel, lang) {
    if (!value) return neverLabel;
    return new Date(value).toLocaleString(lang === "ar" ? "ar-EG" : "en-US");
}

function withCount(template, count) {
    return template.replaceAll("{count}", String(count));
}

function csvCell(value) {
    return `"${String(value || "").replace(/"/g, '""')}"`;
}

const emptyWeddingSettings = {
    groom_en: "",
    bride_en: "",
    groom_ar: "",
    bride_ar: "",
    date_iso: "",
    end_iso: "",
    venue_en: "",
    venue_ar: "",
    hall_en: "",
    hall_ar: "",
    city_en: "",
    city_ar: "",
    address_en: "",
    address_ar: "",
    maps_url: "",
    maps_embed: "",
    note_en: "",
    note_ar: "",
    theme_id: "blossom-gold",
};

function dateTimeLocalValue(value) {
    return String(value || "").slice(0, 16);
}

function withExistingOffset(previousValue, localValue) {
    if (!localValue) return "";
    const offset = String(previousValue || "").match(/(Z|[+-]\d{2}:\d{2})$/)?.[1] || "+03:00";
    return `${localValue}:00${offset}`;
}

function qrFileName(slug) {
    const safe = String(slug || "invitation")
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, "-")
        .replace(/^-+|-+$/g, "");
    return `${safe || "invitation"}-qr.svg`;
}

function QrPreview({ url, slug, testId }) {
    const { t } = useLang();
    const svgRef = useRef(null);
    const [downloadHref, setDownloadHref] = useState("");

    useLayoutEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;
        const clone = svg.cloneNode(true);
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        const markup = new XMLSerializer().serializeToString(clone);
        setDownloadHref(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`);
    }, [url]);

    return (
        <>
            <div className="flex justify-center" data-testid={`${testId}-image`}>
                <QRCodeSVG
                    ref={svgRef}
                    value={url}
                    size={220}
                    marginSize={2}
                    level="M"
                    title={t("adminQrAlt")}
                    bgColor="#ffffff"
                    fgColor="#0f172a"
                />
            </div>
            <a
                href={downloadHref || undefined}
                download={qrFileName(slug)}
                className={`inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 ${downloadHref ? "" : "pointer-events-none opacity-50"}`}
                data-testid={`${testId}-download`}
            >
                <Download size={15} /> {t("adminQrDownload")}
            </a>
        </>
    );
}

function InviteQrDialog({ url, slug, testId, compact }) {
    const { t, dir } = useLang();
    const [open, setOpen] = useState(false);
    const triggerClass = compact
        ? "p-1.5 rounded hover:bg-slate-100 text-slate-600"
        : "inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white text-slate-700 px-3 py-2 text-sm hover:bg-slate-100";

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button type="button" className={triggerClass} title={t("adminQr")} data-testid={testId}>
                    <QrCode size={compact ? 14 : 15} />
                    {!compact && t("adminQr")}
                </button>
            </DialogTrigger>
            <DialogContent className="admin-scope max-w-sm bg-white text-slate-900" dir={dir}>
                <DialogHeader className={dir === "rtl" ? "sm:text-right" : undefined}>
                    <DialogTitle>{t("adminQrTitle")}</DialogTitle>
                    <DialogDescription className="break-all text-slate-500">{url}</DialogDescription>
                </DialogHeader>
                {open && <QrPreview url={url} slug={slug} testId={testId} />}
            </DialogContent>
        </Dialog>
    );
}

function formatWeddingSubtitle(wedding, t, lang) {
    const names = [wedding?.groom_en, wedding?.bride_en].filter(Boolean).join(" & ") || t("adminWeddingFallback");
    const date = wedding?.date_iso
        ? new Date(wedding.date_iso).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US")
        : t("adminDateNotSet");
    return `${names} · ${date}`;
}

export default function AdminDashboard() {
    const nav = useNavigate();
    const { weddingSlug } = useParams();
    const { t, dir, lang } = useLang();
    const adminApi = useCallback((suffix) => `/w/${weddingSlug}/admin${suffix}`, [weddingSlug]);
    const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
    const [stats, setStats] = useState(null);
    const [guests, setGuests] = useState([]);
    const [rsvps, setRsvps] = useState([]);
    const [messages, setMessages] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [wedding, setWedding] = useState(emptyWeddingSettings);
    const [savingWedding, setSavingWedding] = useState(false);

    const [newName, setNewName] = useState("");
    const [newCategory, setNewCategory] = useState("");
    const [bulkNames, setBulkNames] = useState("");
    const [bulkCategory, setBulkCategory] = useState("");
    const [showBulk, setShowBulk] = useState(false);
    const [guestSearch, setGuestSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [savingLinkSentId, setSavingLinkSentId] = useState(null);
    const [savingEnglishNameId, setSavingEnglishNameId] = useState(null);
    const [savingCategoryId, setSavingCategoryId] = useState(null);
    const [editingCategoryId, setEditingCategoryId] = useState(null);

    const refresh = useCallback(async () => {
        if (!weddingSlug) return;
        try {
            const [s, g, r, m, p, w] = await Promise.all([
                api.get(adminApi("/stats")),
                api.get(adminApi("/guests")),
                api.get(adminApi("/rsvps")),
                api.get(adminApi("/messages")),
                api.get(adminApi("/photos")),
                api.get(adminApi("/wedding")),
            ]);
            setStats(s.data);
            setGuests(g.data);
            setRsvps(r.data);
            setMessages(m.data);
            setPhotos(p.data);
            setWedding({ ...emptyWeddingSettings, ...w.data });
        } catch (e) {
            const status = e?.response?.status;
            if (status === 401) {
                nav("/admin/login");
            } else if (status === 403 || status === 404) {
                nav("/admin");
            }
        }
    }, [adminApi, nav, weddingSlug]);

    useEffect(() => {
        if (!localStorage.getItem("wd-admin-token")) {
            nav("/admin/login");
            return;
        }
        refresh();
    }, [nav, refresh]);

    useEffect(() => {
        let cancelled = false;
        api.get("/auth/me")
            .then(({ data }) => {
                if (!cancelled) setIsPlatformAdmin(data?.role === "platform_admin");
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    const addGuest = async () => {
        if (!newName.trim()) return;
        try {
            const { data } = await api.post(adminApi("/guests"), { name: newName.trim(), category: newCategory.trim() });
            setNewName("");
            setNewCategory("");
            if (data.invite_url) {
                await copyText(data.invite_url);
            }
            toast.success(t("adminGuestAdded"));
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("adminError"));
        }
    };

    const addBulk = async () => {
        const names = bulkNames.split("\n").map((s) => s.trim()).filter(Boolean);
        if (!names.length) return;
        try {
            const { data } = await api.post(adminApi("/guests/bulk"), { names, category: bulkCategory.trim() });
            setBulkNames("");
            setBulkCategory("");
            setShowBulk(false);
            toast.success(withCount(t("adminGuestsAdded"), data.created.length));
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("adminError"));
        }
    };

    const del = async (guest) => {
        const message = guest.is_open_invitation_registration
            ? t("adminConfirmOpenRegistration")
            : t("adminConfirmDeleteGuest");
        if (!window.confirm(message)) return;
        try {
            await api.delete(adminApi(`/guests/${guest.id}`));
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("adminError"));
        }
    };

    const deletePhoto = async (id) => {
        if (!window.confirm(t("adminConfirmDeletePhoto"))) return;
        try {
            await api.delete(adminApi(`/photos/${id}`));
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("adminError"));
        }
    };

    const inviteUrlFor = useCallback((slug, provided) => (
        absoluteInviteUrl(provided, slug, weddingSlug)
    ), [weddingSlug]);

    const copyLink = async (slug) => {
        const guest = guests.find((g) => g.slug === slug);
        const url = inviteUrlFor(slug, guest?.invite_url);
        const copied = await copyText(url);
        if (copied) {
            toast.success(t("adminLinkCopied"));
        } else {
            window.prompt(t("adminCopyPrompt"), url);
        }
    };

    const openInvitationUrl = inviteUrlFor("invitation", wedding.open_invitation_url);

    const copyInvitationLink = async () => {
        const url = openInvitationUrl;
        const copied = await copyText(url);
        if (copied) {
            toast.success(t("adminInvitationCopied"));
        } else {
            window.prompt(t("adminCopyPrompt"), url);
        }
    };

    const shareLink = async (guest) => {
        const url = inviteUrlFor(guest.slug, guest.invite_url);
        const text = `${t("adminShareTitle")} — ${guest.name}\n${url}`;
        if (navigator.share) {
            try { await navigator.share({ title: t("adminShareTitle"), text, url }); return; } catch {}
        }
        copyLink(guest.slug);
    };

    const toggleLinkSent = async (guest, checked) => {
        const nextValue = checked === true;
        const previousValue = !!guest.link_sent;
        setSavingLinkSentId(guest.id);
        setGuests((current) => current.map((item) => (
            item.id === guest.id ? { ...item, link_sent: nextValue } : item
        )));
        try {
            const { data } = await api.patch(adminApi(`/guests/${guest.id}/link-sent`), { link_sent: nextValue });
            setGuests((current) => current.map((item) => (
                item.id === guest.id ? { ...item, ...data, rsvp: item.rsvp } : item
            )));
        } catch (e) {
            setGuests((current) => current.map((item) => (
                item.id === guest.id ? { ...item, link_sent: previousValue } : item
            )));
            toast.error(e?.response?.data?.detail || t("adminError"));
        } finally {
            setSavingLinkSentId(null);
        }
    };

    const updateEnglishNameDraft = (guestId, value) => {
        setGuests((current) => current.map((item) => (
            item.id === guestId ? { ...item, english_name: value } : item
        )));
    };

    const saveEnglishName = async (guestId, englishName) => {
        const nextValue = (englishName || "").trim();
        setSavingEnglishNameId(guestId);
        try {
            const { data } = await api.patch(adminApi(`/guests/${guestId}/english-name`), { english_name: nextValue });
            setGuests((current) => current.map((item) => (
                item.id === guestId ? { ...item, ...data, rsvp: item.rsvp } : item
            )));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("adminError"));
            refresh();
        } finally {
            setSavingEnglishNameId(null);
        }
    };

    const updateCategoryDraft = (guestId, value) => {
        setGuests((current) => current.map((item) => (
            item.id === guestId ? { ...item, category: value } : item
        )));
    };

    const saveCategory = async (guestId, category) => {
        const nextValue = (category || "").trim();
        setSavingCategoryId(guestId);
        try {
            const { data } = await api.patch(adminApi(`/guests/${guestId}/category`), { category: nextValue });
            setGuests((current) => current.map((item) => (
                item.id === guestId ? { ...item, ...data, rsvp: item.rsvp } : item
            )));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("adminError"));
            refresh();
        } finally {
            setSavingCategoryId(null);
        }
    };

    const categoryOptions = useMemo(() => {
        return Array.from(new Set(
            guests
                .map((guest) => (guest.category || "").trim())
                .filter(Boolean)
        )).sort((a, b) => a.localeCompare(b));
    }, [guests]);

    const filteredGuests = useMemo(() => {
        const search = guestSearch.trim().toLowerCase();
        return guests.filter((guest) => {
            const category = (guest.category || "").trim();
            if (categoryFilter !== "all" && category !== categoryFilter && guest.id !== editingCategoryId) return false;
            if (!search) return true;
            return [guest.name, guest.english_name].some((value) => (
                String(value || "").toLowerCase().includes(search)
            ));
        });
    }, [categoryFilter, editingCategoryId, guestSearch, guests]);

    const exportFilteredGuestLinks = () => {
        if (!filteredGuests.length) {
            toast.error(t("adminNoExport"));
            return;
        }

        const rows = [
            [t("adminCsvName"), t("adminCsvEnglishName"), t("adminCsvCategory"), t("adminCsvInviteLink")],
            ...filteredGuests.map((guest) => [
                guest.name,
                guest.english_name,
                guest.category,
                inviteUrlFor(guest.slug, guest.invite_url),
            ]),
        ];
        const csv = rows.map((row) => row.map(csvCell).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const stamp = new Date().toISOString().slice(0, 10);
        link.href = url;
        link.download = `guest-links-${stamp}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const logout = () => {
        localStorage.removeItem("wd-admin-token");
        nav("/admin/login");
    };

    const updateWeddingField = (key, value) => {
        setWedding((current) => ({ ...current, [key]: value }));
    };

    const updateWeddingDateField = (key, value) => {
        setWedding((current) => ({ ...current, [key]: withExistingOffset(current[key], value) }));
    };

    const saveWedding = async () => {
        setSavingWedding(true);
        try {
            const { data } = await api.put(adminApi("/wedding"), wedding);
            setWedding({ ...emptyWeddingSettings, ...data });
            toast.success(t("adminSettingsSaved"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("adminSettingsSaveFailed"));
        } finally {
            setSavingWedding(false);
        }
    };

    return (
        <div className="admin-scope min-h-[100dvh] bg-slate-50" dir={dir}>
            <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                        <h1 className="text-lg font-semibold text-slate-900">{t("adminDashboardTitle")}</h1>
                        <p className="text-xs text-slate-500 truncate" data-testid="admin-wedding-subtitle">{formatWeddingSubtitle(wedding, t, lang)}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        {isPlatformAdmin && (
                            <Link to="/admin" className="text-sm text-slate-600 hover:text-slate-900" data-testid="admin-all-weddings-link">
                                {t("adminAllWeddings")}
                            </Link>
                        )}
                        <LanguageToggle />
                        <button onClick={logout} className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5" data-testid="admin-logout-btn">
                            <LogOut size={15} /> {t("logout")}
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 md:px-6 py-8 space-y-8">
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="admin-stats-grid">
                        <Stat label={t("adminStatGuests")} value={stats.total_guests} icon={Users} testId="stat-guests" />
                        <Stat label={t("adminStatOpened")} value={stats.opened_invites || 0} icon={MousePointerClick} testId="stat-opened" />
                        <Stat label={t("adminStatAttending")} value={stats.total_people_attending} icon={BarChart3} testId="stat-attending" />
                        <Stat label={t("adminStatMessages")} value={stats.messages} icon={Mail} testId="stat-messages" />
                        <Stat label={t("adminStatPhotos")} value={stats.photos} icon={ImageIcon} testId="stat-photos" />
                    </div>
                )}

                <Tabs defaultValue="guests" className="w-full">
                    <TabsList className="bg-white border border-slate-200 p-1 rounded-lg">
                        <TabsTrigger value="guests" data-testid="admin-tab-guests" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">{t("adminTabGuests")}</TabsTrigger>
                        <TabsTrigger value="rsvps" data-testid="admin-tab-rsvps" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">{t("adminTabRsvps")}</TabsTrigger>
                        <TabsTrigger value="messages" data-testid="admin-tab-messages" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">{t("adminTabMessages")}</TabsTrigger>
                        <TabsTrigger value="photos" data-testid="admin-tab-photos" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">{t("adminTabPhotos")}</TabsTrigger>
                        <TabsTrigger value="settings" data-testid="admin-tab-settings" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">{t("adminTabSettings")}</TabsTrigger>
                    </TabsList>

                    <TabsContent value="guests" className="mt-4 space-y-4">
                        <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5">
                            <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs uppercase tracking-wider text-slate-500">{t("adminOpenInvitation")}</p>
                                    <p className="mt-1 text-sm text-slate-700 truncate" data-testid="admin-open-invitation-url">{openInvitationUrl}</p>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={copyInvitationLink}
                                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white text-slate-700 px-3 py-2 text-sm hover:bg-slate-100"
                                        data-testid="admin-copy-open-invitation"
                                    >
                                        <Copy size={15} /> {t("adminCopy")}
                                    </button>
                                    <a
                                        href={openInvitationUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white text-slate-700 px-3 py-2 text-sm hover:bg-slate-100"
                                        data-testid="admin-open-open-invitation"
                                    >
                                        <ExternalLink size={15} /> {t("adminOpen")}
                                    </a>
                                    <a
                                        href={whatsappShareUrl(openInvitationUrl)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white text-slate-700 px-3 py-2 text-sm hover:bg-slate-100"
                                        data-testid="admin-open-invitation-whatsapp"
                                    >
                                        {t("adminWhatsapp")}
                                    </a>
                                    <InviteQrDialog url={openInvitationUrl} slug="invitation" testId="admin-open-invitation-qr" />
                                </div>
                            </div>
                            <div className="flex flex-col md:flex-row gap-3">
                                <input
                                    type="text"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && addGuest()}
                                    placeholder={t("adminGuestNamePlaceholder")}
                                    className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm focus:bg-white focus:border-slate-400 outline-none"
                                    data-testid="admin-new-guest-name"
                                />
                                <input
                                    type="text"
                                    value={newCategory}
                                    onChange={(e) => setNewCategory(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && addGuest()}
                                    placeholder={t("adminCategoryPlaceholder")}
                                    className="md:w-48 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm focus:bg-white focus:border-slate-400 outline-none"
                                    data-testid="admin-new-guest-category"
                                />
                                <button onClick={addGuest} className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2.5 text-sm font-medium hover:bg-slate-800" data-testid="admin-add-guest-btn">
                                    <Plus size={16} /> {t("adminAdd")}
                                </button>
                                <button onClick={() => setShowBulk((s) => !s)} className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 text-slate-700 px-4 py-2.5 text-sm hover:bg-slate-100" data-testid="admin-toggle-bulk-btn">
                                    {t("adminBulkAdd")}
                                </button>
                            </div>
                            {showBulk && (
                                <div className="mt-4 space-y-2">
                                    <input
                                        type="text"
                                        value={bulkCategory}
                                        onChange={(e) => setBulkCategory(e.target.value)}
                                        list="admin-bulk-category-options"
                                        placeholder={t("adminBulkCategoryPlaceholder")}
                                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:bg-white focus:border-slate-400 outline-none"
                                        data-testid="admin-bulk-category"
                                    />
                                    <datalist id="admin-bulk-category-options">
                                        {categoryOptions.map((category) => (
                                            <option key={category} value={category} />
                                        ))}
                                    </datalist>
                                    <textarea
                                        value={bulkNames}
                                        onChange={(e) => setBulkNames(e.target.value)}
                                        rows={6}
                                        placeholder={t("adminPasteNamesPlaceholder")}
                                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:bg-white focus:border-slate-400 outline-none"
                                        data-testid="admin-bulk-textarea"
                                    />
                                    <button onClick={addBulk} className="rounded-lg bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-800" data-testid="admin-bulk-submit">
                                        {t("adminAddAll")}
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                            <div className="flex flex-col md:flex-row gap-3 md:items-center">
                                <input
                                    type="search"
                                    value={guestSearch}
                                    onChange={(e) => setGuestSearch(e.target.value)}
                                    placeholder={t("adminFilterPlaceholder")}
                                    className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm focus:bg-white focus:border-slate-400 outline-none"
                                    data-testid="admin-guest-filter-search"
                                />
                                <select
                                    value={categoryFilter}
                                    onChange={(e) => setCategoryFilter(e.target.value)}
                                    className="md:w-56 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 focus:bg-white focus:border-slate-400 outline-none"
                                    data-testid="admin-guest-filter-category"
                                >
                                    <option value="all">{t("adminAllCategories")}</option>
                                    {categoryOptions.map((category) => (
                                        <option key={category} value={category}>{category}</option>
                                    ))}
                                </select>
                                <span className="text-xs text-slate-500 tabular-nums md:w-24 md:text-right">
                                    {filteredGuests.length} / {guests.length}
                                </span>
                                <button
                                    type="button"
                                    onClick={exportFilteredGuestLinks}
                                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 text-slate-700 px-4 py-2.5 text-sm hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={filteredGuests.length === 0}
                                    data-testid="admin-export-filtered-links"
                                >
                                    <Download size={16} /> {t("adminExportCsv")}
                                </button>
                            </div>
                        </div>

                        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto max-w-full">
                            <table className="w-full min-w-[880px] text-sm" dir="ltr">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                                        <th className="px-4 py-3">{t("adminColName")}</th>
                                        <th className="px-4 py-3">{t("adminColCategory")}</th>
                                        <th className="px-4 py-3">{t("adminColEnglishName")}</th>
                                        <th className="px-4 py-3">{t("adminColLinkSent")}</th>
                                        <th className="px-3 py-3 hidden md:table-cell w-40">{t("adminColLink")}</th>
                                        <th className="px-4 py-3">{t("adminColClicks")}</th>
                                        <th className="px-4 py-3">{t("adminColRsvp")}</th>
                                        <th className="px-4 py-3 text-right">{t("adminColActions")}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100" data-testid="admin-guests-tbody">
                                    {guests.length === 0 && (
                                        <tr><td colSpan={8} className="text-center text-slate-500 py-8">{t("adminEmptyGuests")}</td></tr>
                                    )}
                                    {guests.length > 0 && filteredGuests.length === 0 && (
                                        <tr><td colSpan={8} className="text-center text-slate-500 py-8">{t("adminNoFilterMatch")}</td></tr>
                                    )}
                                    {filteredGuests.map((g) => (
                                        <tr key={g.id} className="hover:bg-slate-50">
                                            <td className="px-4 py-3 font-medium text-slate-900">{g.name}</td>
                                            <td className="px-4 py-3 min-w-[160px]">
                                                {g.is_open_invitation_registration ? (
                                                    <span
                                                        className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 px-2.5 py-1 text-xs font-medium"
                                                        data-testid={`admin-guest-category-${g.slug}`}
                                                    >
                                                        {g.category || t("adminOpenLink")}
                                                    </span>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={g.category || ""}
                                                        onChange={(e) => updateCategoryDraft(g.id, e.target.value)}
                                                        onFocus={() => setEditingCategoryId(g.id)}
                                                        onBlur={(e) => {
                                                            setEditingCategoryId(null);
                                                            saveCategory(g.id, e.target.value);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                e.preventDefault();
                                                                setEditingCategoryId(null);
                                                                saveCategory(g.id, e.currentTarget.value);
                                                            }
                                                        }}
                                                        placeholder={t("adminCategoryInputPlaceholder")}
                                                        disabled={savingCategoryId === g.id}
                                                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:bg-white focus:border-slate-400 outline-none disabled:opacity-60"
                                                        data-testid={`admin-guest-category-${g.slug}`}
                                                    />
                                                )}
                                            </td>
                                            <td className="px-4 py-3 min-w-[220px]">
                                                {g.is_open_invitation_registration ? (
                                                    <span
                                                        className="block min-h-9 text-sm text-slate-700"
                                                        data-testid={`admin-guest-english-name-${g.slug}`}
                                                    >
                                                        {g.english_name || ""}
                                                    </span>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={g.english_name || ""}
                                                        onChange={(e) => updateEnglishNameDraft(g.id, e.target.value)}
                                                        onBlur={(e) => saveEnglishName(g.id, e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                e.preventDefault();
                                                                saveEnglishName(g.id, e.currentTarget.value);
                                                            }
                                                        }}
                                                        placeholder={t("adminEnglishNamePlaceholder")}
                                                        disabled={savingEnglishNameId === g.id}
                                                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:bg-white focus:border-slate-400 outline-none disabled:opacity-60"
                                                        data-testid={`admin-guest-english-name-${g.slug}`}
                                                    />
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {g.is_open_invitation_registration ? (
                                                    <span
                                                        className="text-sm text-slate-600"
                                                        data-testid={`admin-guest-link-sent-${g.slug}`}
                                                    >
                                                        {t("adminOpenLink")}
                                                    </span>
                                                ) : (
                                                    <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                                                        <Checkbox
                                                            checked={!!g.link_sent}
                                                            disabled={savingLinkSentId === g.id}
                                                            onCheckedChange={(checked) => toggleLinkSent(g, checked)}
                                                            data-testid={`admin-guest-link-sent-${g.slug}`}
                                                        />
                                                        <span>{g.link_sent ? t("adminSent") : t("adminNotSent")}</span>
                                                    </label>
                                                )}
                                            </td>
                                            <td className="px-3 py-3 text-slate-500 hidden md:table-cell">
                                                <div className="w-40 truncate">{inviteUrlFor(g.slug, g.invite_url)}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                {g.has_clicked ? (
                                                    <div>
                                                        <span className="inline-flex items-center rounded-full bg-sky-50 text-sky-700 px-2 py-0.5 text-xs tabular-nums">
                                                            {g.click_count} {g.click_count === 1 ? t("adminClickOne") : t("adminClickMany")}
                                                        </span>
                                                        <p className="mt-1 text-[11px] text-slate-400 hidden lg:block">{formatClickTime(g.last_clicked_at, t("adminNever"), lang)}</p>
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-400 text-xs">{t("adminNever")}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {g.rsvp ? (
                                                    g.rsvp.attending ? (
                                                        <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-xs">
                                                            ✓ {g.rsvp.num_guests}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center rounded-full bg-rose-50 text-rose-700 px-2 py-0.5 text-xs">
                                                            ✗ {t("adminRsvpNo")}
                                                        </span>
                                                    )
                                                ) : (
                                                    <span className="text-slate-400 text-xs">{t("adminPending")}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                                                    <a
                                                        href={inviteUrlFor(g.slug, g.invite_url)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                                                        title={t("adminOpen")}
                                                        data-testid={`admin-guest-open-${g.slug}`}
                                                    >
                                                        <ExternalLink size={14} />
                                                    </a>
                                                    <InviteQrDialog
                                                        url={inviteUrlFor(g.slug, g.invite_url)}
                                                        slug={g.slug}
                                                        testId={`admin-guest-qr-${g.slug}`}
                                                        compact
                                                    />
                                                    <button onClick={() => copyLink(g.slug)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title={t("adminTitleCopyLink")} data-testid={`admin-guest-copy-${g.slug}`}>
                                                        <Copy size={14} />
                                                    </button>
                                                    <button onClick={() => shareLink(g)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title={t("adminTitleShare")} data-testid={`admin-guest-share-${g.slug}`}>
                                                        <Share2 size={14} />
                                                    </button>
                                                    <button onClick={() => del(g)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" title={t("adminTitleDelete")} data-testid={`admin-guest-delete-${g.slug}`}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </TabsContent>

                    <TabsContent value="rsvps" className="mt-4">
                        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="admin-rsvps-panel">
                            <table className="w-full text-sm" dir="ltr">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                                        <th className="px-4 py-3">{t("adminColGuest")}</th>
                                        <th className="px-4 py-3">{t("adminColAttending")}</th>
                                        <th className="px-4 py-3">{t("adminColCount")}</th>
                                        <th className="px-4 py-3 hidden md:table-cell">{t("adminColNames")}</th>
                                        <th className="px-4 py-3">{t("adminColTime")}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {rsvps.length === 0 && (
                                        <tr><td colSpan={5} className="text-center text-slate-500 py-8">{t("adminEmptyRsvps")}</td></tr>
                                    )}
                                    {rsvps.map((r) => (
                                        <tr key={r.id}>
                                            <td className="px-4 py-3 font-medium text-slate-900">{r.guest_name}</td>
                                            <td className="px-4 py-3">
                                                {r.attending ? (
                                                    <span className="text-emerald-700">{t("adminYes")}</span>
                                                ) : (
                                                    <span className="text-rose-700">{t("adminRsvpNo")}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 tabular-nums">{r.num_guests}</td>
                                            <td className="px-4 py-3 hidden md:table-cell text-slate-600">{(r.guest_names || []).join(", ")}</td>
                                            <td className="px-4 py-3 text-slate-500 text-xs">{new Date(r.submitted_at).toLocaleString(lang === "ar" ? "ar-EG" : "en-US")}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </TabsContent>

                    <TabsContent value="messages" className="mt-4">
                        <div className="grid gap-4 md:grid-cols-2" data-testid="admin-messages-panel">
                            {messages.length === 0 && (
                                <p className="text-slate-500 text-sm col-span-full text-center py-8">{t("adminEmptyMessages")}</p>
                            )}
                            {messages.map((m) => (
                                <div key={m.id} className="bg-white border border-slate-200 rounded-xl p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-semibold text-slate-900">{m.sender_name || m.guest_name}</p>
                                            <p className="text-xs text-slate-500">{new Date(m.created_at).toLocaleString(lang === "ar" ? "ar-EG" : "en-US")}</p>
                                        </div>
                                        <span className="text-xs rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">{m.type}</span>
                                    </div>
                                    {m.type === "text" ? (
                                        <p className="mt-3 text-slate-700 whitespace-pre-wrap">{m.text}</p>
                                    ) : (
                                        <img src={m.drawing_data_url} alt={t("adminDrawnMessageAlt")} className="mt-3 w-full rounded-lg border border-slate-200 bg-white" />
                                    )}
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="photos" className="mt-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-photos-panel">
                            {photos.length === 0 && (
                                <p className="text-slate-500 text-sm col-span-full text-center py-8">{t("adminEmptyPhotos")}</p>
                            )}
                            {photos.map((p) => {
                                const previewUrl = p.url ? `${BACKEND_URL}${p.url}` : null;
                                const targetUrl = previewUrl || p.drive_web_view_link;
                                return (
                                    <div key={p.id} className="aspect-square overflow-hidden rounded-lg bg-slate-100 group relative">
                                        <a href={targetUrl} target="_blank" rel="noopener noreferrer" className="block w-full h-full" data-testid={`admin-photo-open-${p.id}`}>
                                            {previewUrl && p.media_type === "video" ? (
                                                <video src={previewUrl} className="w-full h-full object-cover group-hover:scale-105 transition-transform" muted />
                                            ) : previewUrl ? (
                                                <img src={previewUrl} alt={t("adminUploadedAlt")} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                                            ) : (
                                                <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-500 text-sm px-3 text-center">
                                                    <ExternalLink size={18} />
                                                    {t("adminOpenInDrive")}
                                                </div>
                                            )}
                                            {p.media_type === "video" && (
                                                <span className="absolute left-2 bottom-2 rounded bg-slate-900/80 text-white px-2 py-0.5 text-xs">{t("adminVideo")}</span>
                                            )}
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => deletePhoto(p.id)}
                                            className="absolute right-2 top-2 z-10 rounded-md bg-white/95 p-1.5 text-rose-600 shadow-sm hover:bg-rose-50"
                                            title={t("adminDeletePhoto")}
                                            data-testid={`admin-photo-delete-${p.id}`}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </TabsContent>

                    <TabsContent value="settings" className="mt-4">
                        <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5" data-testid="admin-wedding-settings-panel">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-b border-slate-100 pb-4">
                                <div>
                                    <h2 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
                                        <Settings size={17} /> {t("adminSettingsHeading")}
                                    </h2>
                                    <p className="mt-1 text-sm text-slate-500">{t("adminSettingsHint")}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={saveWedding}
                                    disabled={savingWedding}
                                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:opacity-60"
                                    data-testid="admin-save-wedding-settings"
                                >
                                    <Save size={16} /> {savingWedding ? t("adminSaving") : t("adminSaveSettings")}
                                </button>
                            </div>

                            <div className="mt-5 grid gap-4 md:grid-cols-2">
                                <label className="block md:col-span-2">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminTheme")}</span>
                                    <select
                                        value={themeRegistry[wedding.theme_id] ? wedding.theme_id : "blossom-gold"}
                                        onChange={(e) => updateWeddingField("theme_id", e.target.value)}
                                        className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400"
                                        data-testid="admin-wedding-theme"
                                    >
                                        {Object.keys(themeRegistry).map((id) => (
                                            <option key={id} value={id}>{themeRegistry[id].name}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminGroomEn")}</span>
                                    <input value={wedding.groom_en} onChange={(e) => updateWeddingField("groom_en", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-groom-en" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminBrideEn")}</span>
                                    <input value={wedding.bride_en} onChange={(e) => updateWeddingField("bride_en", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-bride-en" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminGroomAr")}</span>
                                    <input dir="rtl" value={wedding.groom_ar} onChange={(e) => updateWeddingField("groom_ar", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-groom-ar" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminBrideAr")}</span>
                                    <input dir="rtl" value={wedding.bride_ar} onChange={(e) => updateWeddingField("bride_ar", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-bride-ar" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminStartDate")}</span>
                                    <input type="datetime-local" value={dateTimeLocalValue(wedding.date_iso)} onChange={(e) => updateWeddingDateField("date_iso", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-date-iso" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminEndDate")}</span>
                                    <input type="datetime-local" value={dateTimeLocalValue(wedding.end_iso)} onChange={(e) => updateWeddingDateField("end_iso", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-end-iso" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminVenueEn")}</span>
                                    <input value={wedding.venue_en} onChange={(e) => updateWeddingField("venue_en", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-venue-en" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminVenueAr")}</span>
                                    <input dir="rtl" value={wedding.venue_ar} onChange={(e) => updateWeddingField("venue_ar", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-venue-ar" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminHallEn")}</span>
                                    <input value={wedding.hall_en} onChange={(e) => updateWeddingField("hall_en", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-hall-en" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminHallAr")}</span>
                                    <input dir="rtl" value={wedding.hall_ar} onChange={(e) => updateWeddingField("hall_ar", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-hall-ar" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminCityEn")}</span>
                                    <input value={wedding.city_en} onChange={(e) => updateWeddingField("city_en", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-city-en" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminCityAr")}</span>
                                    <input dir="rtl" value={wedding.city_ar} onChange={(e) => updateWeddingField("city_ar", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-city-ar" />
                                </label>
                                <label className="block md:col-span-2">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminAddressEn")}</span>
                                    <input value={wedding.address_en} onChange={(e) => updateWeddingField("address_en", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-address-en" />
                                </label>
                                <label className="block md:col-span-2">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminAddressAr")}</span>
                                    <input dir="rtl" value={wedding.address_ar} onChange={(e) => updateWeddingField("address_ar", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-address-ar" />
                                </label>
                                <label className="block md:col-span-2">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminMapsUrl")}</span>
                                    <input value={wedding.maps_url} onChange={(e) => updateWeddingField("maps_url", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-maps-url" />
                                </label>
                                <label className="block md:col-span-2">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminMapsEmbed")}</span>
                                    <input value={wedding.maps_embed} onChange={(e) => updateWeddingField("maps_embed", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-maps-embed" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminNoteEn")}</span>
                                    <textarea rows={3} value={wedding.note_en} onChange={(e) => updateWeddingField("note_en", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-note-en" />
                                </label>
                                <label className="block">
                                    <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("adminNoteAr")}</span>
                                    <textarea dir="rtl" rows={3} value={wedding.note_ar} onChange={(e) => updateWeddingField("note_ar", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400" data-testid="admin-wedding-note-ar" />
                                </label>
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
